import { ReactNode } from 'react';

import clsxm from '@/lib/clsxm';
import { ClickTrackingValues } from '@/lib/tracking';
import { trackClick } from '@/lib/tracking';
import { useGlobalContext } from '@/hooks/use-global-context';
import { useSectionContext } from '@/hooks/use-section-context';

import { IconColor, IconSize } from '@/components/icons/base-icon';
import PlusIcon from '@/components/icons/plus-icon';

import { Side } from '@/enums/side';

export enum LinkVariant {
  LINK = 'LINK',
  LINK_BLACK = 'LINK_BLACK',
  LINK_WITH_PLUS = 'LINK_WITH_PLUS',
  PRIMARY_BUTTON = 'PRIMARY_BUTTON',
  WHITE_BUTTON = 'WHITE_BUTTON',
  GRAY_BUTTON = 'GRAY_BUTTON',
  LINK_NOTHING = 'LINK_NOTHING',
}

const commonButtonStyles =
  'block rounded-full border py-2 px-10 text-center text-base font-medium w-full sm:w-auto sm:inline-block transition-all duration-300';

export const LinkVariantStyles: Record<LinkVariant, string> = {
  [LinkVariant.LINK]: 'text-primary-extra-dark hover:text-primary',
  [LinkVariant.LINK_BLACK]: 'text-black hover:text-primary-dark',
  [LinkVariant.LINK_WITH_PLUS]:
    'text-primary hover:text-primary-dark tracking-[.35px] font-medium',
  [LinkVariant.PRIMARY_BUTTON]: `${commonButtonStyles} border-transparent bg-primary text-primary-contrast hover:bg-primary-light-poppy hover:shadow-hover`,
  [LinkVariant.WHITE_BUTTON]: `${commonButtonStyles} bg-white text-typography border-black hover:bg-grey100`,
  [LinkVariant.GRAY_BUTTON]: `${commonButtonStyles} bg-grey100 text-typography border-black hover:bg-grey300`,
  [LinkVariant.LINK_NOTHING]: '',
};

export type LinkProps = {
  href: string;
  openInNewWindow?: boolean;
  children: string | ReactNode;
  id?: string;
  variant?: LinkVariant;
  onClickTrackEvent?: () => void;
  icon?: string | ReactNode;
  iconPosition?: Side;
  className?: string;
  trackingValues?: ClickTrackingValues;
} & React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

export const Link: React.FC<LinkProps> = ({
  href,
  openInNewWindow,
  id,
  variant = LinkVariant.LINK,
  icon,
  iconPosition,
  children,
  className,
  trackingValues,
  ...rest
}) => {
  // TODO: build a content renderer for the icons and position and label
  const { pageMeta } = useGlobalContext();
  const { sectionProps } = useSectionContext();

  const ctaCopy = typeof children === 'string' ? children : '';

  if (variant === LinkVariant.LINK_WITH_PLUS) {
    icon = (
      <PlusIcon
        inline
        size={IconSize.XS}
        color={IconColor.INHERIT}
        className='mt-[4px]'
      />
    );
    iconPosition = Side.LEFT;
  }

  const trackClickEvent = () => {
    if (trackingValues?.clickTrachingEnabled) {
      trackClick({
        pageMeta,
        sectionProps,
        trackingValues,
        ctaCopy,
      });
    }
  };

  const targetProps = openInNewWindow
    ? { target: '_blank', rel: 'noopener' }
    : {};

  return (
    <a
      href={href || '#'}
      id={id}
      className={clsxm(
        'transition-colors',
        LinkVariantStyles[variant],
        className
      )}
      {...targetProps}
      style={{ wordBreak: 'break-word' }}
      onClick={() => trackClickEvent()}
      {...rest}
    >
      {icon && iconPosition === Side.LEFT && (
        <span className='link-icon'>{icon}</span>
      )}

      <span className='link-content'>{children}</span>

      {icon && iconPosition === Side.RIGHT && (
        <span className='link-icon'>{icon}</span>
      )}
    </a>
  );
};
export default Link;
